import {Set} from 'immutable';
import React, {Component, ReactNode} from 'react';

import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AccordionSection, AccordionSectionData} from './accordionSection';

/*
 * Props and State.
 */

export interface AccordionProps {
  className?: string;
  sections: ReadonlyArray<AccordionSectionData>;
  allowMultiSelect?: boolean;
  disableBorders?: boolean;
  renderSectionTitle?: (title: ReactNode, onClick: ClickEventHandler, isOpen: boolean) => ReactNode;
}

interface AccordionState {
  expandedSections: Set<string>;
}

/*
 * Component.
 */

export class Accordion extends Component<AccordionProps, AccordionState> {
  constructor(props: AccordionProps) {
    super(props);

    this.state = {
      expandedSections: this.findDefaultExpandedSections(),
    };
  }

  private findDefaultExpandedSections(): Set<string> {
    const expandedSections = this.props.sections
      .filter((section) => section.isExpandedByDefault)
      .map((section) => section.id);

    // Only expand the first section by default in single-select mode.
    if (!this.props.allowMultiSelect) {
      return Set(expandedSections.slice(0, 1));
    }

    return Set(expandedSections);
  }

  private toggleSectionExpanded = (sectionId: string) => {
    this.setState(
      (state, props) => {
        if (state.expandedSections.has(sectionId)) {
          return {
            expandedSections: state.expandedSections.delete(sectionId),
          };
        }

        return {
          expandedSections: props.allowMultiSelect
            ? state.expandedSections.add(sectionId)
            : state.expandedSections.clear().add(sectionId),
        };
      },
      () => this.fireOnSectionToggled(sectionId),
    );
  };

  private fireOnSectionToggled = (sectionId: string) => {
    const wasExpanded: boolean = this.state.expandedSections.has(sectionId);
    const section: AccordionSectionData | undefined = this.findSection(sectionId);

    if (section?.onSectionToggled) {
      section.onSectionToggled(wasExpanded);
    }
  };

  private findSection = (sectionId: string): AccordionSectionData | undefined =>
    this.props.sections.find((section) => section.id === sectionId);

  private renderSection = (sectionData: AccordionSectionData) => (
    <AccordionSection
      key={sectionData.id}
      id={sectionData.id}
      title={sectionData.title}
      titleAction={sectionData.titleAction}
      content={sectionData.content}
      isExpanded={this.state.expandedSections.has(sectionData.id)}
      sectionPadding={sectionData.sectionPadding}
      titlePadding={sectionData.titlePadding}
      onTitleClicked={() => this.toggleSectionExpanded(sectionData.id)}
      shouldScrollOnExpand={sectionData.shouldScrollOnExpand}
      disableBorders={this.props.disableBorders}
      renderSectionTitle={this.props.renderSectionTitle}
    />
  );

  render() {
    return <div className={this.props.className}>{this.props.sections.map(this.renderSection)}</div>;
  }
}
